<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'
import { useFormat } from '@/composables/useFormat'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Inventory',
  },
})

const ability = useAbility()
const { request, loading } = useApiRequest()
const { formatRupiah, formatNumber } = useFormat()

// ─── State ────────────────────────────────────────────────────────────────────
const ingredients = ref([])
const totalItems = ref(0)
const categories = ref([])

const options = ref({
  page: 1,
  itemsPerPage: 25,
  sortBy: [],
})
const search = ref('')
const filterCategory = ref(null)
const filterLowStock = ref(false)
const filterActive = ref(null) // null = semua, true = aktif, false = nonaktif

const deleteDialog = ref(false)
const selectedItem = ref(null)

// ─── Table Headers ─────────────────────────────────────────────────────────────
const headers = [
  { title: 'Nama Bahan', key: 'name', minWidth: '180px' },
  { title: 'SKU', key: 'sku', width: '120px' },
  { title: 'Kategori', key: 'category', width: '140px', sortable: false },
  { title: 'Stok Saat Ini', key: 'current_stock', width: '140px', align: 'end' },
  { title: 'Min Stok', key: 'minimum_stock', width: '110px', align: 'end' },
  { title: 'HPP Rata-rata', key: 'average_cost', width: '140px', align: 'end' },
  { title: 'Nilai Stok', key: 'stock_value', width: '140px', align: 'end' },
  { title: 'Status', key: 'is_active', width: '100px', sortable: false },
  { title: 'Aksi', key: 'actions', width: '130px', sortable: false, align: 'center' },
]

// ─── Fetch Data ────────────────────────────────────────────────────────────────
const fetchIngredients = async () => {
  const query = {
    page: options.value.page,
    per_page: options.value.itemsPerPage,
  }
  if (search.value) query.search = search.value
  if (filterCategory.value) query.category_id = filterCategory.value
  if (filterLowStock.value) query.low_stock = 1
  if (filterActive.value !== null) query.active_only = filterActive.value ? 1 : 0

  const response = await useApi('/api/v1/ingredients', { query }).get()
  const data = response?.data ?? response

  ingredients.value = data?.data ?? []
  totalItems.value = data?.pagination?.total ?? 0
}

const fetchCategories = async () => {
  const response = await useApi('/api/v1/categories', {
    query: { per_page: 100 },
  }).get()
  const data = response?.data ?? response
  categories.value = data?.data ?? []
}

// ─── Delete ────────────────────────────────────────────────────────────────────
const confirmDelete = item => {
  selectedItem.value = item
  deleteDialog.value = true
}

const deleteIngredient = async () => {
  await request(async () => {
    await useApi(`/api/v1/ingredients/${selectedItem.value.id}`).delete()
    deleteDialog.value = false
    await fetchIngredients()
  })
}

// ─── Toggle Active ─────────────────────────────────────────────────────────────
const toggleActive = async item => {
  await request(async () => {
    await useApi(`/api/v1/ingredients/${item.id}`).put({
      body: { is_active: !item.is_active },
    })
    await fetchIngredients()
  })
}

// ─── Reset Filter ──────────────────────────────────────────────────────────────
const resetFilters = () => {
  search.value = ''
  filterCategory.value = null
  filterLowStock.value = false
  filterActive.value = null
  options.value.page = 1
}

// ─── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  await fetchCategories()
  await fetchIngredients()
})

watch(
  [options, search, filterCategory, filterLowStock, filterActive],
  fetchIngredients,
  { deep: true },
)

// debounce search
let searchTimer
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    options.value.page = 1
  }, 400)
})
</script>

<template>
  <div>
    <!-- Page Header -->
    <VRow class="mb-4">
      <VCol cols="12" class="d-flex align-center justify-space-between flex-wrap gap-3">
        <div>
          <h4 class="text-h4 font-weight-medium mb-1">Bahan Baku</h4>
          <p class="text-body-2 text-medium-emphasis mb-0">
            Kelola inventori bahan baku dan stok
          </p>
        </div>
        <VBtn
          v-if="ability.can('create', 'Inventory')"
          color="primary"
          prepend-icon="ri-add-line"
          :to="{ name: 'inventory-ingredients-create' }"
        >
          Tambah Bahan
        </VBtn>
      </VCol>
    </VRow>

    <!-- Alert Low Stock -->
    <VAlert
      v-if="filterLowStock"
      type="warning"
      variant="tonal"
      class="mb-4"
      icon="ri-alert-line"
      closable
      @click:close="filterLowStock = false"
    >
      Menampilkan bahan baku dengan stok di bawah minimum.
    </VAlert>

    <!-- Main Card -->
    <VCard>
      <!-- Toolbar -->
      <VCardText class="pb-0">
        <VRow align="center" class="gap-y-3">
          <!-- Search -->
          <VCol cols="12" sm="4">
            <VTextField
              v-model="search"
              placeholder="Cari nama / SKU..."
              prepend-inner-icon="ri-search-line"
              density="compact"
              hide-details
              clearable
            />
          </VCol>

          <!-- Filter Kategori -->
          <VCol cols="6" sm="3">
            <VSelect
              v-model="filterCategory"
              :items="categories"
              item-title="name"
              item-value="id"
              placeholder="Semua Kategori"
              density="compact"
              hide-details
              clearable
            />
          </VCol>

          <!-- Filter Status -->
          <VCol cols="6" sm="2">
            <VSelect
              v-model="filterActive"
              :items="[
                { title: 'Semua Status', value: null },
                { title: 'Aktif', value: true },
                { title: 'Nonaktif', value: false },
              ]"
              item-title="title"
              item-value="value"
              density="compact"
              hide-details
            />
          </VCol>

          <!-- Filter Low Stock -->
          <VCol cols="auto">
            <VBtn
              :variant="filterLowStock ? 'tonal' : 'outlined'"
              :color="filterLowStock ? 'warning' : 'default'"
              size="small"
              prepend-icon="ri-alert-line"
              @click="filterLowStock = !filterLowStock; options.page = 1"
            >
              Stok Rendah
            </VBtn>
          </VCol>

          <!-- Reset Filter -->
          <VCol cols="auto">
            <VBtn
              variant="text"
              size="small"
              prepend-icon="ri-refresh-line"
              @click="resetFilters"
            >
              Reset
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>

      <VDivider class="mt-4" />

      <!-- Table -->
      <VDataTableServer
        v-model:options="options"
        :headers="headers"
        :items="ingredients"
        :loading="loading"
        :items-length="totalItems"
        hover
        class="text-no-wrap"
      >
        <!-- Name -->
        <template #item.name="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.name }}</div>
            <div v-if="item.is_low_stock" class="d-flex align-center gap-1 mt-1">
              <VChip size="x-small" color="warning" variant="tonal">
                <VIcon start icon="ri-alert-line" size="10" />
                Stok Rendah
              </VChip>
            </div>
          </div>
        </template>

        <!-- SKU -->
        <template #item.sku="{ item }">
          <span class="text-mono text-body-2">{{ item.sku ?? '-' }}</span>
        </template>

        <!-- Kategori -->
        <template #item.category="{ item }">
          <VChip
            v-if="item.category"
            size="small"
            variant="tonal"
            :color="item.category.color ?? 'primary'"
          >
            {{ item.category.name }}
          </VChip>
          <span v-else class="text-disabled">-</span>
        </template>

        <!-- Stok Saat Ini -->
        <template #item.current_stock="{ item }">
          <span
            :class="[
              'font-weight-medium',
              item.is_low_stock ? 'text-warning' : 'text-high-emphasis',
            ]"
          >
            {{ formatNumber(item.current_stock) }}
            <span class="text-medium-emphasis text-caption ms-1">{{ item.unit?.symbol }}</span>
          </span>
        </template>

        <!-- Min Stok -->
        <template #item.minimum_stock="{ item }">
          <span class="text-medium-emphasis">
            {{ formatNumber(item.minimum_stock) }}
            <span class="text-caption ms-1">{{ item.unit?.symbol }}</span>
          </span>
        </template>

        <!-- HPP Rata-rata -->
        <template #item.average_cost="{ item }">
          {{ formatRupiah(item.average_cost) }}
        </template>

        <!-- Nilai Stok -->
        <template #item.stock_value="{ item }">
          <span class="font-weight-medium">{{ formatRupiah(item.stock_value) }}</span>
        </template>

        <!-- Status -->
        <template #item.is_active="{ item }">
          <VChip
            :color="item.is_active ? 'success' : 'error'"
            size="small"
            variant="tonal"
          >
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>

        <!-- Aksi -->
        <template #item.actions="{ item }">
          <!-- Detail / Movements -->
          <VBtn
            icon
            size="small"
            variant="text"
            color="info"
            :to="{ name: 'inventory-ingredients-id', params: { id: item.id } }"
          >
            <VIcon icon="ri-eye-line" />
            <VTooltip activator="parent">Lihat Detail & Riwayat Stok</VTooltip>
          </VBtn>

          <!-- Edit -->
          <VBtn
            v-if="ability.can('update', 'Inventory')"
            icon
            size="small"
            variant="text"
            :to="{ name: 'inventory-ingredients-id-edit', params: { id: item.id } }"
          >
            <VIcon icon="ri-edit-line" />
            <VTooltip activator="parent">Edit</VTooltip>
          </VBtn>

          <!-- Delete -->
          <VBtn
            v-if="ability.can('delete', 'Inventory')"
            icon
            size="small"
            variant="text"
            color="error"
            @click="confirmDelete(item)"
          >
            <VIcon icon="ri-delete-bin-line" />
            <VTooltip activator="parent">Hapus</VTooltip>
          </VBtn>
        </template>

        <!-- No Data -->
        <template #no-data>
          <div class="text-center pa-10">
            <VIcon icon="ri-archive-line" size="56" color="grey-lighten-1" class="mb-3" />
            <p class="text-body-1 font-weight-medium mb-1">Belum ada bahan baku</p>
            <p class="text-body-2 text-medium-emphasis mb-4">
              Tambahkan bahan baku pertama untuk mulai mencatat inventori.
            </p>
            <VBtn
              v-if="ability.can('create', 'Inventory')"
              color="primary"
              prepend-icon="ri-add-line"
              :to="{ name: 'inventory-ingredients-create' }"
            >
              Tambah Bahan Baku
            </VBtn>
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <!-- Delete Dialog -->
    <VDialog v-model="deleteDialog" max-width="480">
      <VCard>
        <VCardTitle class="text-h6 pa-6 pb-3">
          <VIcon icon="ri-delete-bin-line" color="error" class="me-2" />
          Hapus Bahan Baku?
        </VCardTitle>
        <VCardText class="px-6 py-2">
          Apakah Anda yakin ingin menghapus bahan baku
          <strong>{{ selectedItem?.name }}</strong>?
          <VAlert type="warning" variant="tonal" density="compact" class="mt-3">
            Bahan baku tidak dapat dihapus jika sudah memiliki riwayat stok atau digunakan dalam resep.
          </VAlert>
        </VCardText>
        <VCardActions class="px-6 pb-4">
          <VSpacer />
          <VBtn variant="text" @click="deleteDialog = false">Batal</VBtn>
          <VBtn color="error" :loading="loading" @click="deleteIngredient">Hapus</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
